import { useRef, useState } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useToast } from './Toast';
import { Modal } from './Modal';
import { Button } from './Button';
import { MenuImportar } from './MenuImportar';

const CONFIRMAR_REEMPLAZAR_BASE = 'REEMPLAZAR BASE';
const CONFIRMAR_BORRAR_INACTIVOS = 'BORRAR INACTIVOS';

// ---- Dachshund import loader ----
function DachshundLoader({ pct }: { pct: number }) {
  const bones = [0, 20, 40, 60, 80, 100];
  return (
    <div className="py-6 px-4 select-none">
      <p className="text-center text-sm font-medium text-slate-600 mb-5">
        Importando base de datos… <span className="text-brand-600 font-bold">{pct}%</span>
      </p>
      <div className="relative h-12 bg-amber-50 rounded-full border-2 border-amber-200 overflow-hidden mx-2">
        <div className="absolute inset-y-0 left-0 bg-amber-100 transition-all duration-300" style={{ width: `${pct}%` }} />
        {bones.map(b => (
          <div key={b} className={`absolute top-1/2 -translate-y-1/2 text-lg transition-all duration-300 ${pct >= b ? 'opacity-0 scale-50' : 'opacity-100 scale-100'}`} style={{ left: `calc(${b}% + 4px)` }}>🦴</div>
        ))}
        <div className="absolute top-1/2 -translate-y-1/2 transition-all duration-300 text-2xl" style={{ left: `calc(${Math.min(pct, 96)}% - 20px)` }}>
          <span style={{ display: 'inline-block', transform: 'scaleX(1)' }}>🐕</span>
        </div>
      </div>
      <p className="text-center text-xs text-slate-400 mt-3">
        {pct < 30 ? 'Leyendo el archivo…' : pct < 60 ? 'Cargando artículos…' : pct < 90 ? 'Guardando proveedores y códigos…' : '¡Casi listo!'}
      </p>
    </div>
  );
}

/** Configuración → Mercado Libre y catálogo: importar artículos y (sólo admin) mantenimiento de la Base. */
export function ImportacionesYMantenimientoBase({ esAdmin }: { esAdmin: boolean }) {
  const { can } = useAuth();
  const toast = useToast();

  const [importing, setImporting] = useState(false);
  const [importandoArchivo, setImportandoArchivo] = useState(false);
  const [importPct, setImportPct] = useState(0);
  const importTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [reemplazarFile, setReemplazarFile] = useState<File | null>(null);
  const [reemplazarFrase, setReemplazarFrase] = useState('');
  const [reemplazarError, setReemplazarError] = useState('');
  const [reemplazando, setReemplazando] = useState(false);
  const [borrarInactivosOpen, setBorrarInactivosOpen] = useState(false);
  const [borrarInactivosFrase, setBorrarInactivosFrase] = useState('');
  const [borrarInactivosError, setBorrarInactivosError] = useState('');
  const [borrandoInactivos, setBorrandoInactivos] = useState(false);
  const reemplazarInputRef = useRef<HTMLInputElement | null>(null);

  const closeReemplazarBase = () => { setReemplazarFile(null); setReemplazarFrase(''); setReemplazarError(''); };

  const confirmReemplazarBase = async () => {
    if (!reemplazarFile) return;
    if (reemplazarFrase.trim() !== CONFIRMAR_REEMPLAZAR_BASE) return;
    setReemplazando(true);
    try {
      const r = await api.upload<any>('/api/v3/catalogo/reemplazar-base', reemplazarFile, { confirmacion: CONFIRMAR_REEMPLAZAR_BASE });
      const tot = (r.catalogoCreados||0) + (r.catalogoActualizados||0);
      toast.addToast(`✓ Base reemplazada: ${tot} artículos (${r.catalogoCreados||0} nuevos, ${r.catalogoActualizados||0} actualizados) · ${r.componentesCreados||0} componentes nuevos · ${r.catalogoDesactivados||0} desactivados${r.fallos ? ` · ${r.fallos} errores` : ''}`, r.fallos ? 'info' : 'success');
      if (r.errores?.length) console.warn('Errores:', r.errores);
      closeReemplazarBase();
    } catch (e) {
      setReemplazarError(extractErrorMessage(e));
    } finally {
      setReemplazando(false);
    }
  };

  const closeBorrarInactivos = () => { setBorrarInactivosOpen(false); setBorrarInactivosFrase(''); setBorrarInactivosError(''); };

  const confirmBorrarInactivos = async () => {
    if (borrarInactivosFrase.trim() !== CONFIRMAR_BORRAR_INACTIVOS) return;
    setBorrandoInactivos(true);
    try {
      const r = await api.post<any>('/api/v3/catalogo/inactivos/borrar', { confirmacion: CONFIRMAR_BORRAR_INACTIVOS });
      toast.addToast(`✓ ${r.eliminados||0} SKUs inactivos eliminados de la Base`, 'success');
      closeBorrarInactivos();
    } catch (e) {
      setBorrarInactivosError(extractErrorMessage(e));
    } finally {
      setBorrandoInactivos(false);
    }
  };

  // Importar desde archivo: alta y actualización de artículos con la plantilla (CSV o XLSX).
  const importarArchivo = async (f: File) => {
    if (importing || importandoArchivo) return;
    setImportandoArchivo(true);
    try {
      const r = await api.upload<{ creados?: number; actualizados?: number; fallos?: number; errores?: unknown[] }>('/api/v3/catalogo/importar', f);
      const partes = [`${r.creados || 0} creados`, `${r.actualizados || 0} actualizados`];
      if (r.fallos) partes.push(`${r.fallos} con errores`);
      toast.addToast(`Importación de artículos: ${partes.join(', ')}`, r.fallos ? 'info' : 'success');
      if (r.errores?.length) console.warn('Errores:', r.errores);
    } catch (err) {
      toast.addToast(extractErrorMessage(err), 'error');
    } finally {
      setImportandoArchivo(false);
    }
  };

  // Importar 02_CODIGOS: el Excel de la Base de datos (hoja 02_CODIGOS), con la barra de progreso.
  const importarBase = async (f: File) => {
    if (importing || importandoArchivo) return;
    setImporting(true); setImportPct(0);
    let pct = 0;
    importTimerRef.current = setInterval(() => { pct = pct < 85 ? pct + Math.random() * 4 + 1 : pct; setImportPct(Math.min(Math.round(pct), 85)); }, 400);
    try {
      const r = await api.upload<any>('/api/v3/catalogo/importar-base', f);
      if (importTimerRef.current) clearInterval(importTimerRef.current);
      setImportPct(100); await new Promise(res => setTimeout(res, 800));
      const tot = (r.catalogoCreados||0) + (r.catalogoActualizados||0);
      toast.addToast(`✓ ${tot} artículos (${r.catalogoCreados||0} nuevos, ${r.catalogoActualizados||0} actualizados) · ${r.componentesCreados||0} componentes nuevos · ${r.articulosProveedorUpserted||0} códigos proveedor · ${r.proveedoresCreados||0} proveedores nuevos${r.fallos ? ` · ${r.fallos} errores` : ''}`, r.fallos ? 'info' : 'success');
      if (r.errores?.length) console.warn('Errores:', r.errores);
    } catch(err) { if (importTimerRef.current) clearInterval(importTimerRef.current); toast.addToast(extractErrorMessage(err), 'error'); }
    finally { setImporting(false); setImportPct(0); }
  };

  return (
    <>
      <section aria-label="Importaciones" className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-700">Importaciones</h2>
        <MenuImportar alinear="izquierda" ocupado={importing || importandoArchivo} opciones={[
          {
            etiqueta: 'Importar desde archivo',
            descripcion: 'Alta y actualización de artículos desde la plantilla (CSV o XLSX)',
            archivo: { accept: '.csv,.xlsx', onArchivo: (f) => void importarArchivo(f) },
            deshabilitado: !can('catalogo', 'crear'),
          },
          {
            etiqueta: 'Importar 02_CODIGOS',
            descripcion: 'Excel de la Base de datos (hoja 02_CODIGOS)',
            archivo: { accept: '.xlsx', onArchivo: (f) => void importarBase(f) },
            deshabilitado: !can('catalogo', 'crear'),
          },
          {
            etiqueta: 'Descargar plantilla',
            descripcion: 'Modelo de planilla para importar artículos',
            onClick: () => window.open('/api/v3/catalogo/plantilla?format=xlsx', '_blank'),
          },
        ]} />
      </section>

      {esAdmin && can('catalogo', 'eliminar') && (
        <section aria-label="Mantenimiento de la Base" className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-700">Mantenimiento de la Base</h2>
          <div className="flex flex-wrap items-center gap-2">
            <input ref={reemplazarInputRef} type="file" accept=".xlsx" disabled={reemplazando} onChange={e => {
              const f = e.target.files?.[0]; e.target.value = ''; if (!f || reemplazando) return;
              setReemplazarFile(f); setReemplazarFrase(''); setReemplazarError('');
            }} className="hidden" />
            <button
              onClick={() => reemplazarInputRef.current?.click()}
              disabled={reemplazando}
              className="text-sm text-slate-700 border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Reemplaza la Base con el Excel exportado desde Insumos: crea/actualiza lo que viene en el archivo y desactiva lo que no está"
            >
              Reemplazar base…
            </button>
            <button
              onClick={() => { setBorrarInactivosFrase(''); setBorrarInactivosError(''); setBorrarInactivosOpen(true); }}
              disabled={borrandoInactivos}
              className="text-sm text-red-600 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Elimina definitivamente de la Base los SKUs marcados como inactivos"
            >
              Borrar inactivos…
            </button>
          </div>
        </section>
      )}

      {importing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-6 pt-6 pb-2"><h2 className="text-base font-semibold text-slate-800 text-center">Importando Base de datos</h2></div>
            <DachshundLoader pct={importPct} />
          </div>
        </div>
      )}

      <Modal open={!!reemplazarFile} onClose={closeReemplazarBase} title="Reemplazar base" size="sm">
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <p className="font-semibold mb-1">⚠️ Esta acción reemplaza la Base completa</p>
            <p>Se van a crear/actualizar los SKUs del archivo <strong>{reemplazarFile?.name}</strong> y se van a <strong>desactivar</strong> los SKUs activos que no estén en él.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Escribí REEMPLAZAR BASE para confirmar</label>
            <input
              type="text"
              aria-label="Escribí REEMPLAZAR BASE para confirmar"
              value={reemplazarFrase}
              onChange={e => { setReemplazarFrase(e.target.value); setReemplazarError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') confirmReemplazarBase(); }}
              disabled={reemplazando}
              autoFocus
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            {reemplazarError && <p className="text-red-600 text-xs mt-1">{reemplazarError}</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={closeReemplazarBase} disabled={reemplazando}>Cancelar</Button>
            <Button onClick={confirmReemplazarBase} disabled={reemplazando || reemplazarFrase.trim() !== CONFIRMAR_REEMPLAZAR_BASE}>
              {reemplazando ? 'Reemplazando…' : 'Reemplazar base'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={borrarInactivosOpen} onClose={closeBorrarInactivos} title="Borrar inactivos" size="sm">
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            <p className="font-semibold mb-1">⚠️ Esta acción es irreversible</p>
            <p>Se van a eliminar <strong>permanentemente</strong> de la Base todos los SKUs marcados como inactivos (no es una desactivación, se borran los registros).</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Escribí BORRAR INACTIVOS para confirmar</label>
            <input
              type="text"
              aria-label="Escribí BORRAR INACTIVOS para confirmar"
              value={borrarInactivosFrase}
              onChange={e => { setBorrarInactivosFrase(e.target.value); setBorrarInactivosError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') confirmBorrarInactivos(); }}
              disabled={borrandoInactivos}
              autoFocus
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            {borrarInactivosError && <p className="text-red-600 text-xs mt-1">{borrarInactivosError}</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={closeBorrarInactivos} disabled={borrandoInactivos}>Cancelar</Button>
            <Button onClick={confirmBorrarInactivos} disabled={borrandoInactivos || borrarInactivosFrase.trim() !== CONFIRMAR_BORRAR_INACTIVOS}>
              {borrandoInactivos ? 'Borrando…' : 'Borrar inactivos'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
